import { Component, OnInit, signal, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { CheckoutService } from '../../services/checkout.service';
import { SettingsService } from '../../services/settings.service';
import { AuthService } from '../../services/auth.service';
import { CartService } from '../../services/cart.service';
import { 
  ArkifiHubCheckoutRequestDto, 
  ArkifiHubCheckoutResponseDto,
  CheckoutProductDto,
  PickupLocationDto,
  OrderStatus,
  ApiResponse,
  PaymentMethod,
  ShoppingCardPaymentResponseDto,
  DeliveryOption,
  SavedDeliveryAddressDto,
  UpdateSavedDeliveryAddressRequestDto
} from '../../models/checkout.dto';
import type { TerminalAfricaStateItemDto, TerminalAfricaCityItemDto } from '../../services/checkout.service';
import { ShoppingCardService } from '../../services/shopping-card.service';
import { CustomerCardService, ArkifiHubCustomerCardDto } from '../../services/customer-card.service';
import { ArkifiHubPlatformService } from '../../services/arkifihub-platform.service';

interface CheckoutProduct {
  productId: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  productImage?: string;
}

@Component({
  selector: 'app-checkout',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    ProgressSpinnerModule,
    ToastModule
  ],
  providers: [MessageService],
  templateUrl: './checkout.component.html',
  styleUrl: './checkout.component.css'
})
export class CheckoutComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private checkoutService = inject(CheckoutService);
  private settingsService = inject(SettingsService);
  private authService = inject(AuthService);
  private cartService = inject(CartService);
  private messageService = inject(MessageService);
  private shoppingCardService = inject(ShoppingCardService);
  private customerCardService = inject(CustomerCardService);
  private arkifiHubPlatformService = inject(ArkifiHubPlatformService);

  // Step tracking
  currentStep = signal(1);
  totalSteps = 4; // Review Order, Delivery Option, Pick Location/Payment Method, Complete Payment

  // Data
  platformId = signal<string>('');
  platformName = signal<string>('');
  products = signal<CheckoutProduct[]>([]);
  pickupLocations = signal<PickupLocationDto[]>([]);
  availableStates = signal<string[]>([]);
  selectedLocation = signal<PickupLocationDto | null>(null);
  
  // Delivery options
  deliveryOption = signal<'pickup' | 'delivery'>('pickup');
  /** Full delivery address string built from state + city + line (used for quote and checkout). */
  deliveryAddress = signal<string>('');
  /** Street/building part of delivery address (user input). */
  deliveryAddressLine = signal<string>('');
  /** Terminal Africa states for delivery address dropdown. */
  deliveryStates = signal<TerminalAfricaStateItemDto[]>([]);
  /** Terminal Africa cities for selected state. */
  deliveryCities = signal<TerminalAfricaCityItemDto[]>([]);
  selectedDeliveryState = signal<TerminalAfricaStateItemDto | null>(null);
  selectedDeliveryCity = signal<TerminalAfricaCityItemDto | null>(null);
  isLoadingDeliveryStates = signal(false);
  isLoadingDeliveryCities = signal(false);
  hasLogisticsProvider = signal<boolean>(false);
  isLoadingLogisticsCheck = signal<boolean>(false);
  
  // Delivery quotes
  deliveryQuotes = signal<any[]>([]);
  selectedDeliveryCompany = signal<any | null>(null);
  isLoadingQuotes = signal(false);
  deliveryFee = signal<number>(0);
  
  // Payment method
  selectedPaymentMethod = signal<PaymentMethod | null>(null);
  transactionPin = signal<string>('');
  shoppingCardBalance = signal<number>(0);
  isLoadingBalance = signal(false);
  
  // Saved cards (only when platform supports Paystack/Monnify)
  supportsSavedCards = signal<boolean>(false);
  savedCards = signal<ArkifiHubCustomerCardDto[]>([]);
  selectedCardId = signal<string | null>(null);
  isLoadingCards = signal(false);
  
  // Saved delivery addresses (pop out the address user used / can choose from)
  savedDeliveryAddresses = signal<SavedDeliveryAddressDto[]>([]);
  selectedSavedAddress = signal<SavedDeliveryAddressDto | null>(null);
  isLoadingSavedAddresses = signal(false);
  /** Address being edited in modal (null = modal closed). */
  editingAddress = signal<SavedDeliveryAddressDto | null>(null);
  /** Form model for edit address modal (bound with ngModel). */
  editAddressFormModel: UpdateSavedDeliveryAddressRequestDto | null = null;
  isSavingAddress = signal(false);
  isDeletingAddress = signal(false);
  
  // Loading states
  isLoadingLocations = signal(false);
  isProcessingPayment = signal(false);
  
  // Payment method enum
  PaymentMethod = PaymentMethod;
  DeliveryOption = DeliveryOption;

  // Computed
  subtotal = computed(() => {
    return this.products().reduce((sum, p) => sum + p.totalPrice, 0);
  });

  totalAmount = computed(() => {
    const subtotal = this.subtotal();
    const delivery = this.deliveryOption() === 'delivery' ? this.deliveryFee() : 0;
    return subtotal + delivery;
  });

  canUseShoppingCard = computed(() => {
    return this.shoppingCardBalance() >= this.totalAmount();
  });

  OrderStatus = OrderStatus;

  ngOnInit(): void {
    // Check if user is authenticated
    if (!this.authService.isAuthenticated()) {
      this.router.navigate(['/signin'], { 
        queryParams: { returnUrl: this.router.url } 
      });
      return;
    }

    // Get platform ID from route
    this.route.params.subscribe(params => {
      const id = params['id'];
      if (id) {
        this.platformId.set(id);
      } else {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Platform ID is required'
        });
        this.router.navigate(['/']);
        return;
      }
    });

    // Load products from cart
    this.loadCartProducts();
    
    // Load shopping card balance
    this.loadShoppingCardBalance();
    
    // Check if platform supports saved cards (Paystack/Monnify), then load saved cards if so
    this.loadSupportsSavedCardsAndCards();
    
    // Check logistics provider availability
    this.checkLogisticsProvider();
  }
  
  // Check if logistics provider is configured
  checkLogisticsProvider(): void {
    const platformId = this.platformId();
    if (!platformId) return;
    
    this.isLoadingLogisticsCheck.set(true);
    this.checkoutService.checkLogisticsProvider(platformId).subscribe({
      next: (response) => {
        if (response.success) {
          this.hasLogisticsProvider.set(response.data || false);
        } else {
          this.hasLogisticsProvider.set(false);
        }
      },
      error: (error) => {
        console.error('Error checking logistics provider:', error);
        this.hasLogisticsProvider.set(false);
      },
      complete: () => {
        this.isLoadingLogisticsCheck.set(false);
      }
    });
  }
  
  // Load shopping card balance
  loadShoppingCardBalance(): void {
    const platformId = this.platformId();
    if (!platformId) return;
    
    this.isLoadingBalance.set(true);
    const environment = this.settingsService.getEnvironment();
    
    this.shoppingCardService.getShoppingCard(platformId, environment).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.shoppingCardBalance.set(response.data.balance || 0);
        }
      },
      error: (error) => {
        console.error('Error loading shopping card balance:', error);
        this.shoppingCardBalance.set(0);
      },
      complete: () => {
        this.isLoadingBalance.set(false);
      }
    });
  }

  // Load supports-saved-cards for platform, then load saved cards only when supported
  loadSupportsSavedCardsAndCards(): void {
    const platformId = this.platformId();
    if (!platformId) return;
    
    const environment = this.settingsService.getEnvironment();
    this.arkifiHubPlatformService.getSupportsSavedCards(platformId, environment).subscribe({
      next: (res) => {
        const supported = res.data?.supportsSavedCards ?? false;
        this.supportsSavedCards.set(supported);
        if (supported) {
          this.loadSavedCards();
        } else {
          this.savedCards.set([]);
          this.selectedCardId.set(null);
        }
      },
      error: () => {
        this.supportsSavedCards.set(false);
        this.savedCards.set([]);
      }
    });
  }

  // Load saved cards (call only when platform supports saved cards)
  async loadSavedCards(): Promise<void> {
    const platformId = this.platformId();
    if (!platformId) return;
    
    this.isLoadingCards.set(true);
    try {
      const environment = this.settingsService.getEnvironment();
      const result = await this.customerCardService.getCustomerCards(platformId, environment);
      
      if (result.success && result.data) {
        // Filter only active cards
        const activeCards = result.data.filter(card => card.isActive);
        this.savedCards.set(activeCards);
      }
    } catch (error) {
      console.error('Error loading saved cards:', error);
    } finally {
      this.isLoadingCards.set(false);
    }
  }

  getMaskedCardNumber(last4?: string): string {
    if (!last4) return '**** **** **** ****';
    return `**** **** **** ${last4}`;
  }
  
  // Select payment method
  selectPaymentMethod(method: PaymentMethod): void {
    this.selectedPaymentMethod.set(method);
    // Clear saved card selection when switching to shopping card
    if (method === PaymentMethod.ShoppingCard) {
      this.selectedCardId.set(null);
      // Focus on PIN input after a short delay
      setTimeout(() => {
        const pinInput = document.getElementById('transactionPin') as HTMLInputElement;
        if (pinInput) {
          pinInput.focus();
        }
      }, 100);
    }
  }
  
  // Get shopping card balance formatted
  getShoppingCardBalance(): string {
    return this.formatPrice(this.shoppingCardBalance());
  }

  // Load products from cart
  loadCartProducts(): void {
    const platformId = this.platformId();
    if (!platformId) return;

    this.cartService.getCartSummary(platformId).subscribe({
      next: (response) => {
        if (response.success && response.data && response.data.items.length > 0) {
          // Convert cart items to checkout products
          const checkoutProducts: CheckoutProduct[] = response.data.items.map(item => ({
            productId: item.productId,
            productName: item.productName,
            quantity: item.quantity,
            unitPrice: item.unitPrice,
            totalPrice: item.totalPrice,
            productImage: item.productImage
          }));
          
          this.products.set(checkoutProducts);
          
          // Load pickup locations after products are loaded
          this.loadPickupLocations();
        } else {
          // No products in cart, redirect back
          this.messageService.add({
            severity: 'warn',
            summary: 'Empty Cart',
            detail: 'Your cart is empty. Please add products to checkout.'
          });
          this.router.navigate(['/']);
        }
      },
      error: (error) => {
        console.error('Error loading cart:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load cart items'
        });
        this.router.navigate(['/']);
      }
    });
  }

  // Load pickup locations
  loadPickupLocations(): void {
    this.isLoadingLocations.set(true);
    const platformId = this.platformId();
    this.checkoutService.getPickupLocations(platformId).subscribe({
      next: (response) => {
        if (response.success) {
          const locations = response.data.filter(loc => loc.isActive);
          this.pickupLocations.set(locations);
          this.availableStates.set([...new Set(locations.map(loc => loc.state))].sort());
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: response.message || 'Failed to load pickup locations'
          });
        }
      },
      error: (error) => {
        console.error('Error loading pickup locations:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load pickup locations'
        });
      },
      complete: () => {
        this.isLoadingLocations.set(false);
      }
    });
  }

  /** Build full delivery address from state, city, and street line for quotes/shipment. */
  getFullDeliveryAddress(): string {
    const line = this.deliveryAddressLine().trim();
    const state = this.selectedDeliveryState();
    const city = this.selectedDeliveryCity();
    if (!line || !state || !city) return '';
    return `${line}, ${city.name}, ${state.name}, Nigeria`;
  }

  /** Update deliveryAddress signal from state/city/line (so quote and validation use it). */
  updateFullDeliveryAddress(): void {
    this.deliveryAddress.set(this.getFullDeliveryAddress());
  }

  /** Pending saved address to apply once states finish loading (when user selects address before states load). */
  private pendingSavedAddress = signal<SavedDeliveryAddressDto | null>(null);

  /** Load Terminal Africa states for delivery address dropdown. */
  loadDeliveryStates(onComplete?: () => void): void {
    const platformId = this.platformId();
    if (!platformId) return;
    this.isLoadingDeliveryStates.set(true);
    this.deliveryStates.set([]);
    this.selectedDeliveryState.set(null);
    this.selectedDeliveryCity.set(null);
    this.deliveryCities.set([]);
    this.updateFullDeliveryAddress();
    this.checkoutService.getDeliveryStates(platformId).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.deliveryStates.set(response.data);
        }
      },
      error: () => this.deliveryStates.set([]),
      complete: () => {
        this.isLoadingDeliveryStates.set(false);
        onComplete?.();
      }
    });
  }

  /** Options when state change is triggered from selecting a saved address (keeps selection, runs callback when cities load). */
  private deliveryStateChangeOptions?: { fromSavedAddress?: boolean; afterCitiesLoaded?: (cities: TerminalAfricaCityItemDto[]) => void };

  /** Load Terminal Africa cities when state is selected. */
  onDeliveryStateChange(state: TerminalAfricaStateItemDto | null, options?: { fromSavedAddress?: boolean; afterCitiesLoaded?: (cities: TerminalAfricaCityItemDto[]) => void }): void {
    if (!options?.fromSavedAddress) {
      this.clearSelectedSavedAddress();
    }
    this.deliveryStateChangeOptions = options;
    this.selectedDeliveryState.set(state);
    this.selectedDeliveryCity.set(null);
    this.deliveryCities.set([]);
    this.deliveryQuotes.set([]);
    this.selectedDeliveryCompany.set(null);
    this.deliveryFee.set(0);
    this.updateFullDeliveryAddress();
    if (!state) return;
    const platformId = this.platformId();
    if (!platformId) return;
    this.isLoadingDeliveryCities.set(true);
    this.checkoutService.getDeliveryCities(platformId, state.isoCode).subscribe({
      next: (response) => {
        const cities = response.success && response.data ? response.data : [];
        this.deliveryCities.set(cities);
        this.deliveryStateChangeOptions?.afterCitiesLoaded?.(cities);
      },
      error: () => {
        this.deliveryCities.set([]);
        this.deliveryStateChangeOptions?.afterCitiesLoaded?.([]);
      },
      complete: () => {
        this.isLoadingDeliveryCities.set(false);
        this.deliveryStateChangeOptions = undefined;
      }
    });
  }

  onDeliveryCityChange(city: TerminalAfricaCityItemDto | null): void {
    this.clearSelectedSavedAddress();
    this.selectedDeliveryCity.set(city);
    this.updateFullDeliveryAddress();
    this.deliveryQuotes.set([]);
    this.selectedDeliveryCompany.set(null);
    this.deliveryFee.set(0);
    const loc = this.selectedLocation();
    if (loc && this.getFullDeliveryAddress().length >= 10) {
      this.fetchDeliveryQuote();
    }
  }

  onDeliveryAddressLineChange(): void {
    this.clearSelectedSavedAddress();
    this.updateFullDeliveryAddress();
    this.deliveryQuotes.set([]);
    this.selectedDeliveryCompany.set(null);
    this.deliveryFee.set(0);
    const loc = this.selectedLocation();
    if (loc && this.getFullDeliveryAddress().length >= 10) {
      this.fetchDeliveryQuote();
    }
  }

  // Filter locations by state
  filterByState(event: Event): void {
    const state = (event.target as HTMLSelectElement).value;
    const platformId = this.platformId();
    
    // If state is selected, use the filtered endpoint; otherwise reload all
    if (state) {
      this.checkoutService.getPickupLocationsByState(state, platformId).subscribe({
        next: (response) => {
          if (response.success) {
            const locations = response.data.filter(loc => loc.isActive);
            this.pickupLocations.set(locations);
          }
        },
        error: (error) => {
          console.error('Error filtering locations by state:', error);
        }
      });
    } else {
      // Reload all locations when "All States" is selected
      this.loadPickupLocations();
    }
  }

  // Select pickup location
  selectPickupLocation(location: PickupLocationDto): void {
    this.selectedLocation.set(location);
    
    // If delivery option is selected and full address is ready, fetch delivery quote
    if (this.deliveryOption() === 'delivery' && this.getFullDeliveryAddress().length >= 10) {
      this.fetchDeliveryQuote();
    }
  }

  // Fetch delivery quote from logistics provider
  fetchDeliveryQuote(): void {
    const location = this.selectedLocation();
    const address = this.getFullDeliveryAddress();
    const platformId = this.platformId();
    
    if (!location || !address || address.length < 10 || !platformId) {
      return;
    }

    this.isLoadingQuotes.set(true);
    this.deliveryQuotes.set([]);
    this.selectedDeliveryCompany.set(null);
    this.deliveryFee.set(0);

    const quoteRequest = {
      storeLocationId: location.storeLocationId,
      deliveryAddress: address,
      totalAmount: this.subtotal(),
      packageWeight: 1.0
    };

    this.checkoutService.getDeliveryQuote(quoteRequest, platformId).subscribe({
      next: (response) => {
        if (response.success && response.data?.companies) {
          this.deliveryQuotes.set(response.data.companies);
          // Auto-select first company if available
          if (response.data.companies.length > 0) {
            this.selectDeliveryCompany(response.data.companies[0]);
          }
        } else {
          this.messageService.add({
            severity: 'warn',
            summary: 'Delivery Quote',
            detail: response.message || 'Unable to get delivery quotes'
          });
        }
      },
      error: (error) => {
        console.error('Error fetching delivery quote:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to fetch delivery quotes'
        });
      },
      complete: () => {
        this.isLoadingQuotes.set(false);
      }
    });
  }

  // Select delivery company
  selectDeliveryCompany(company: any): void {
    this.selectedDeliveryCompany.set(company);
    this.deliveryFee.set(company.deliveryFee || 0);
  }

  // Handle delivery address line change (street input) - quotes updated via onDeliveryAddressLineChange / onDeliveryCityChange
  onDeliveryAddressChange(): void {
    this.onDeliveryAddressLineChange();
  }

  /** Switch to delivery option and load states for address dropdown; load saved addresses. */
  selectDeliveryOption(): void {
    this.deliveryOption.set('delivery');
    this.loadDeliveryStates();
    this.loadSavedDeliveryAddresses();
  }

  /** Load saved delivery addresses for the current user (for display and to avoid duplicate Terminal/Arkifi records). */
  loadSavedDeliveryAddresses(): void {
    const platformId = this.platformId();
    const user = this.authService.getUser();
    const email = user?.emailAddress;
    if (!platformId || !email) return;
    this.isLoadingSavedAddresses.set(true);
    const environment = this.settingsService.getEnvironment();
    this.checkoutService.getSavedDeliveryAddresses(email, platformId, environment).subscribe({
      next: (response) => {
        if (response.success && response.data?.length) {
          this.savedDeliveryAddresses.set(response.data);
        } else {
          this.savedDeliveryAddresses.set([]);
        }
      },
      error: () => this.savedDeliveryAddresses.set([]),
      complete: () => this.isLoadingSavedAddresses.set(false)
    });
  }

  /** Select a saved address to pre-fill state, city, and street address in the form. */
  selectSavedAddress(addr: SavedDeliveryAddressDto): void {
    this.deliveryAddressLine.set(addr.line1 || '');
    this.selectedSavedAddress.set(addr);
    const stateList = this.deliveryStates();
    if (stateList.length === 0) {
      this.pendingSavedAddress.set(addr);
      this.loadDeliveryStates(() => {
        const a = this.pendingSavedAddress();
        this.pendingSavedAddress.set(null);
        if (a) this.selectSavedAddress(a);
      });
      return;
    }
    const state = stateList.find(s =>
      (addr.state && (s.name?.toLowerCase() === addr.state.toLowerCase() || s.isoCode?.toLowerCase() === addr.state.toLowerCase()))
    );
    if (state) {
      this.onDeliveryStateChange(state, {
        fromSavedAddress: true,
        afterCitiesLoaded: (cities) => {
          const city = cities.find(c => addr.city && c.name?.toLowerCase() === addr.city!.toLowerCase());
          if (city) this.selectedDeliveryCity.set(city);
          this.updateFullDeliveryAddress();
          this.deliveryQuotes.set([]);
          this.selectedDeliveryCompany.set(null);
          this.deliveryFee.set(0);
          if (this.selectedLocation() && this.getFullDeliveryAddress().length >= 10) {
            this.fetchDeliveryQuote();
          }
        }
      });
    } else {
      this.updateFullDeliveryAddress();
    }
  }

  /** Clear selected saved address (user is typing a new one). */
  clearSelectedSavedAddress(): void {
    this.selectedSavedAddress.set(null);
  }

  /** Open edit modal for a saved address. */
  openEditAddress(addr: SavedDeliveryAddressDto, event?: Event): void {
    event?.stopPropagation();
    this.editingAddress.set(addr);
    this.editAddressFormModel = {
      providerAddressId: addr.providerAddressId,
      line1: addr.line1 ?? '',
      line2: addr.line2 ?? '',
      city: addr.city ?? '',
      state: addr.state ?? '',
      country: addr.country ?? 'NG',
      zip: addr.zip ?? '',
      firstName: addr.firstName ?? '',
      lastName: addr.lastName ?? '',
      email: addr.email ?? '',
      phone: addr.phone ?? '',
      isResidential: addr.isResidential ?? true
    };
  }

  closeEditAddress(): void {
    this.editingAddress.set(null);
    this.editAddressFormModel = null;
  }

  /** Save updated address (Terminal Africa + Arkifi DB). */
  async saveEditedAddress(): Promise<void> {
    const form = this.editAddressFormModel;
    const addr = this.editingAddress();
    if (!form || !addr) return;
    if (!form.line1?.trim() || !form.city?.trim() || !form.state?.trim()) {
      this.messageService.add({ severity: 'warn', summary: 'Validation', detail: 'Street, city and state are required.' });
      return;
    }
    const user = this.authService.getUser();
    const email = user?.emailAddress;
    const platformId = this.platformId();
    const environment = this.settingsService.getEnvironment();
    if (!email || !platformId) return;
    this.isSavingAddress.set(true);
    try {
      const result = await this.checkoutService.updateSavedDeliveryAddress(email, platformId, environment, form).toPromise();
      if (result?.success && result.data) {
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Address updated on Terminal Africa and in your saved addresses.' });
        const list = this.savedDeliveryAddresses().map(a => a.providerAddressId === result.data!.providerAddressId ? result.data! : a);
        this.savedDeliveryAddresses.set(list);
        this.closeEditAddress();
      } else {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: result?.message ?? 'Failed to update address.' });
      }
    } catch (e) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update address.' });
    } finally {
      this.isSavingAddress.set(false);
    }
  }

  /** Delete saved address from Arkifi DB (soft delete). */
  async deleteAddress(addr: SavedDeliveryAddressDto, event?: Event): Promise<void> {
    event?.stopPropagation();
    if (!confirm('Remove this saved address? It will be removed from your saved addresses.')) return;
    const user = this.authService.getUser();
    const email = user?.emailAddress;
    const platformId = this.platformId();
    const environment = this.settingsService.getEnvironment();
    if (!email || !platformId) return;
    this.isDeletingAddress.set(true);
    try {
      const result = await this.checkoutService.deleteSavedDeliveryAddress(email, platformId, environment, addr.providerAddressId).toPromise();
      if (result?.success) {
        this.savedDeliveryAddresses.set(this.savedDeliveryAddresses().filter(a => a.providerAddressId !== addr.providerAddressId));
        if (this.selectedSavedAddress()?.providerAddressId === addr.providerAddressId) this.clearSelectedSavedAddress();
        this.messageService.add({ severity: 'success', summary: 'Removed', detail: 'Address removed from your saved addresses.' });
      } else {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: result?.message ?? 'Failed to delete address.' });
      }
    } catch (e) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete address.' });
    } finally {
      this.isDeletingAddress.set(false);
    }
  }

  getDeliveryStateByIsoCode(isoCode: string): TerminalAfricaStateItemDto | null {
    if (!isoCode) return null;
    return this.deliveryStates().find(s => s.isoCode === isoCode) ?? null;
  }

  getDeliveryCityByName(name: string): TerminalAfricaCityItemDto | null {
    if (!name) return null;
    return this.deliveryCities().find(city => city.name === name) ?? null;
  }

  // Navigation steps
  nextStep(): void {
    if (this.currentStep() < this.totalSteps) {
      // Validate before moving forward
      if (this.currentStep() === 1 && this.products().length === 0) {
        this.messageService.add({
          severity: 'warn',
          summary: 'No Products',
          detail: 'Please select products to checkout'
        });
        return;
      }
      if (this.currentStep() === 2) {
        // Store location is always required (for pickup or as ship-from location for delivery)
        if (!this.selectedLocation()) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Location Required',
            detail: this.deliveryOption() === 'pickup' 
              ? 'Please select a pickup location' 
              : 'Please select a store to ship from'
          });
          return;
        }
        if (this.deliveryOption() === 'delivery') {
          const address = this.getFullDeliveryAddress();
          if (!this.selectedDeliveryState() || !this.selectedDeliveryCity() || !this.deliveryAddressLine().trim() || address.length < 10) {
            this.messageService.add({
              severity: 'warn',
              summary: 'Address Required',
              detail: 'Please select state, city, and enter your street address'
            });
            return;
          }
        }
      }
      if (this.currentStep() === 3) {
        if (!this.selectedPaymentMethod()) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Payment Method Required',
            detail: 'Please select a payment method'
          });
          return;
        }
        if (this.selectedPaymentMethod() === PaymentMethod.ShoppingCard) {
          const pin = this.transactionPin().trim();
          if (!pin || pin.length !== 4 || !/^\d{4}$/.test(pin)) {
            this.messageService.add({
              severity: 'warn',
              summary: 'Transaction PIN Required',
              detail: 'Please enter a valid 4-digit transaction PIN'
            });
            return;
          }
        }
        // PIN is always required, even for PaymentGateway with saved card
        if (this.selectedPaymentMethod() === PaymentMethod.PaymentGateway && this.selectedCardId()) {
          const pin = this.transactionPin().trim();
          if (!pin || pin.length !== 4 || !/^\d{4}$/.test(pin)) {
            this.messageService.add({
              severity: 'warn',
              summary: 'Transaction PIN Required',
              detail: 'Please enter a valid 4-digit transaction PIN'
            });
            return;
          }
        }
      }
      this.currentStep.set(this.currentStep() + 1);
    }
  }

  previousStep(): void {
    if (this.currentStep() > 1) {
      this.currentStep.set(this.currentStep() - 1);
    }
  }

  // Process payment
  async processPayment(): Promise<void> {
    // Validate payment method
    if (!this.selectedPaymentMethod()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please select a payment method'
      });
      return;
    }

    // Validate transaction PIN - always required
    const pin = this.transactionPin().trim();
    if (!pin || pin.length !== 4 || !/^\d{4}$/.test(pin)) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please enter a valid 4-digit transaction PIN'
      });
      return;
    }
    
    // If using saved card, validate card selection
    if (this.selectedPaymentMethod() === PaymentMethod.PaymentGateway && this.selectedCardId()) {
      if (!this.selectedCardId()) {
        this.messageService.add({
          severity: 'error',
          summary: 'Validation Error',
          detail: 'Please select a saved card'
        });
        return;
      }
    }

    // Validate store location (always required)
    if (!this.selectedLocation()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: this.deliveryOption() === 'pickup' 
          ? 'Please select a pickup location'
          : 'Please select a store to ship from'
      });
      return;
    }

    // Validate delivery address if delivery option selected
    if (this.deliveryOption() === 'delivery') {
      const address = this.getFullDeliveryAddress();
      if (!this.selectedDeliveryState() || !this.selectedDeliveryCity() || !this.deliveryAddressLine().trim() || address.length < 10) {
        this.messageService.add({
          severity: 'error',
          summary: 'Validation Error',
          detail: 'Please select state, city, and enter your street address'
        });
        return;
      }
    }

    this.isProcessingPayment.set(true);

    const platformId = this.platformId();
    const environment = this.settingsService.getEnvironment();
    // Backend enum: Test = 1, Live = 2
    const environmentType = environment === 'Live' ? 2 : 1;

    // Determine address: use full delivery address (state + city + line) if delivery, otherwise use pickup location address
    const address = this.deliveryOption() === 'delivery' 
      ? this.getFullDeliveryAddress()
      : (this.selectedLocation()?.address || '');

    const checkoutRequest: ArkifiHubCheckoutRequestDto = {
      paymentMethod: this.selectedPaymentMethod()!,
      totalAmount: this.totalAmount(), // Send total including delivery fee (subtotal + deliveryFee)
      products: this.products().map(p => ({
        productId: p.productId,
        quantity: p.quantity
      })),
      storeLocationId: this.selectedLocation()?.storeLocationId || '00000000-0000-0000-0000-000000000000', // Always need storeLocationId for pickup address
      address: this.deliveryOption() === 'delivery' ? address : undefined,
      deliveryOption: this.deliveryOption() === 'delivery' ? DeliveryOption.Delivery : DeliveryOption.Pickup,
      deliveryFee: this.deliveryOption() === 'delivery' ? this.deliveryFee() : undefined,
      deliveryCompanyName: this.deliveryOption() === 'delivery' && this.selectedDeliveryCompany() 
        ? this.selectedDeliveryCompany().companyName 
        : undefined,
      redirectUrl: this.selectedPaymentMethod() === PaymentMethod.PaymentGateway && (!this.supportsSavedCards() || !this.selectedCardId())
        ? `${window.location.origin}/store/${platformId}/${encodeURIComponent(this.getPlatformName())}/payment-callback`
        : undefined,
      transactionPin: this.transactionPin().trim(), // PIN is always required
      cardId: this.supportsSavedCards() && this.selectedPaymentMethod() === PaymentMethod.PaymentGateway && this.selectedCardId()
        ? this.selectedCardId()!
        : undefined,
      platformId: platformId,
      environmentType: environmentType
    };

    try {
      let response: ApiResponse<ArkifiHubCheckoutResponseDto | ShoppingCardPaymentResponseDto>;
      
      if (this.selectedPaymentMethod() === PaymentMethod.ShoppingCard) {
        console.log('💳 Processing shopping card payment');
        response = await this.checkoutService.processShoppingCardCheckout(checkoutRequest).toPromise() as ApiResponse<ShoppingCardPaymentResponseDto>;
        
        if (response?.success && response.data) {
          const shoppingCardResponse = response.data as ShoppingCardPaymentResponseDto;
          
          // Clear cart after successful order
          this.cartService.clearCart(platformId).subscribe({
            next: () => {
              console.log('Cart cleared after successful order');
            },
            error: (err) => {
              console.error('Error clearing cart:', err);
            }
          });
          
          // Update shopping card balance
          this.shoppingCardBalance.set(shoppingCardResponse.remainingBalance);
          
          // Clear shopping card cache
          this.shoppingCardService.clearCache(platformId, environment);
          
          // Redirect to shopping card success page
          this.router.navigate(['/shopping-card-success'], {
            queryParams: {
              orderId: shoppingCardResponse.orderId,
              platformId: platformId,
              platformName: encodeURIComponent(this.getPlatformName()),
              remainingBalance: shoppingCardResponse.remainingBalance,
              amountDeducted: shoppingCardResponse.amountDeducted
            }
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Payment Failed',
            detail: response?.message || 'Payment processing failed'
          });
        }
      } else {
        console.log('🏦 Processing payment gateway payment');
        response = await this.checkoutService.processPaymentGatewayCheckout(checkoutRequest).toPromise() as ApiResponse<ArkifiHubCheckoutResponseDto>;
        
        if (response?.success && response.data) {
          const gatewayResponse = response.data as ArkifiHubCheckoutResponseDto;
          
          if (gatewayResponse.paymentUrl) {
            // Redirect to payment gateway
            console.log('🏦 Redirecting to payment gateway:', gatewayResponse.paymentUrl);
            window.location.href = gatewayResponse.paymentUrl;
          } else {
            // Payment successful without redirect (saved card payment)
            this.cartService.clearCart(platformId).subscribe({
              next: () => {
                console.log('Cart cleared after successful order');
              },
              error: (err) => {
                console.error('Error clearing cart:', err);
              }
            });
            
            this.messageService.add({
              severity: 'success',
              summary: 'Payment Successful',
              detail: gatewayResponse.message || 'Your order has been placed successfully!'
            });
            
            // Redirect to store page after successful saved card payment
            const platformName = this.getPlatformName();
            this.router.navigate(['/store', platformId, platformName]);
          }
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Payment Failed',
            detail: response?.message || 'Payment processing failed'
          });
        }
      }
    } catch (error: any) {
      console.error('Payment processing error:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Payment Error',
        detail: error?.error?.message || 'Payment processing failed'
      });
    } finally {
      this.isProcessingPayment.set(false);
    }
  }
  
  // Get platform name
  getPlatformName(): string {
    return this.platformName() || 'Store';
  }

  // Format price
  formatPrice(price: number): string {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN'
    }).format(price);
  }

  // Get product image
  getProductImage(product: CheckoutProduct): string {
    return product.productImage || '/assets/placeholder-product.png';
  }

  // Cancel checkout - navigate back to store page
  cancelCheckout(): void {
    // Use browser history to go back to previous page (should be the store page)
    // If history is not available, navigate to marketplace
    if (window.history.length > 1) {
      window.history.back();
    } else {
      this.router.navigate(['/']);
    }
  }
}
